import { BlankTemplate, INVALID_CATEGORY_ID } from './constants';
import { getCurrentActiveCommonTemplate, getCurrentImprintAreaKey, getItemUseScene7Template } from '../main/selectors';
import { getSelectedColorBomOptions, replaceFullPreviewUrlPlaceholder } from '../preview/selectors';
import { getTemplateCanvasUrl, getTemplateThumbnailUrl } from '../utils/scene7url';

import { TemplateType } from './constants';
import { createSelector } from 'reselect';
import { getSelectedImprintAreaKeys } from '../imprintarea/selectors';

export const getCategories = createSelector(
  getCurrentImprintAreaKey,
  (state) => state.categoryDict,
  (imprintAreaKey, categoryDict) => {
    return Object.keys(categoryDict).includes(imprintAreaKey) ? categoryDict[imprintAreaKey] : [];
  }
);

export const getSelectedCategoryId = createSelector(getCategories, (categories) => {
  const selectedCategory = categories.find((category) => category.isSelected);
  return selectedCategory
    ? selectedCategory.categoryId
    : categories.length > 0
    ? categories[0].categoryId
    : INVALID_CATEGORY_ID;
});

const getCommonTemplateDict = createSelector(
  getCurrentImprintAreaKey,
  (state) => state.templateDict,
  (imprintAreaKey, templateDict) => {
    return Object.keys(templateDict).includes(imprintAreaKey) ? templateDict[imprintAreaKey] : {};
  }
);

export const getIfCommonTemplateDictIsNotEmpty = createSelector(getCommonTemplateDict, (templateDict) => {
  return Object.values(templateDict).some((templates) => templates.length > 0);
});

export const getSelectedCommonTemplate = createSelector(getCommonTemplateDict, (templateDict) => {
  return Object.values(templateDict)
    .flat()
    .find((template) => template.isSelected);
});

const compareTemplatesByDesignTitle = (templateA, templateB) => {
  const titleA = templateA.designTitle.toUpperCase();
  const titleB = templateB.designTitle.toUpperCase();

  if (titleA < titleB) {
    return -1;
  } else if (titleA > titleB) {
    return 1;
  } else {
    return 0;
  }
};

export const getCommonTemplates = createSelector(
  getCommonTemplateDict,
  (_, searchBy) => searchBy,
  (templateDict, searchBy) => {
    if (searchBy.keyword !== '') {
      const keyword = searchBy.keyword.toUpperCase();
      const matchedTemplates = Object.values(templateDict)
        .flat()
        .filter((template) => template.designKeywords.toUpperCase().indexOf(keyword) >= 0);
      const selectedTemplate = matchedTemplates.find((template) => template.isSelected);
      const initialUniqueTemplates = selectedTemplate ? [selectedTemplate] : [];
      return matchedTemplates
        .reduce(
          (uniqueTemplates, currentTemplate) =>
            uniqueTemplates.findIndex((template) => template.templateId === currentTemplate.templateId) >= 0
              ? uniqueTemplates
              : [...uniqueTemplates, currentTemplate],
          initialUniqueTemplates
        )
        .sort(compareTemplatesByDesignTitle);
    } else {
      const categoryId = searchBy.categoryId.toString();
      return Object.keys(templateDict).includes(categoryId) ? templateDict[categoryId] : [];
    }
  }
);

export const getSpecialTemplates = createSelector(
  getCurrentImprintAreaKey,
  (state) => state.specialTemplateDict,
  (imprintAreaKey, specialTemplateDict) => {
    return Object.keys(specialTemplateDict).includes(imprintAreaKey) ? specialTemplateDict[imprintAreaKey] : [];
  }
);

export const getSelectedSpecialTemplate = createSelector(getSpecialTemplates, (specialTemplates) => {
  return specialTemplates.find((template) => template.isSelected);
});

export const getCanvasBackground = createSelector(
  (state, imprintAreaKey) => imprintAreaKey || getCurrentImprintAreaKey(state),
  (state) => state.customBackgroundDict,
  (currentCanvasKey, customBackgroundDict) => {
    return customBackgroundDict[currentCanvasKey];
  }
);

export const getSelectedTemplate = createSelector(
  getSelectedSpecialTemplate,
  getSelectedCommonTemplate,
  getCanvasBackground,
  (specialTemplate, commonTemplate, customBackground) => {
    return specialTemplate
      ? specialTemplate.templateType === TemplateType.CUSTOM
        ? customBackground
        : specialTemplate
      : commonTemplate;
  }
);

export const getFinalTemplateSelection = createSelector(
  getSelectedImprintAreaKeys,
  (state) => state.specialTemplateDict,
  (state) => state.templateDict,
  (state) => state.customBackgroundDict,
  (imprintAreaKeys, specialTemplateDict, commonTemplateDict, customBackgroundDict) => {
    const entries = [];
    imprintAreaKeys.forEach((key) => {
      const specialTemplates = Object.keys(specialTemplateDict).includes(key) ? specialTemplateDict[key] : [];
      const specialTemplate = specialTemplates.find((template) => template.isSelected);

      const subCommonTemplateDict = Object.keys(commonTemplateDict).includes(key) ? commonTemplateDict[key] : {};
      const commonTemplate = Object.values(subCommonTemplateDict)
        .flat()
        .find((template) => template.isSelected);

      const customBackground = Object.keys(customBackgroundDict).includes(key) ? customBackgroundDict[key] : {};

      entries.push([
        key,
        specialTemplate
          ? specialTemplate.templateType === TemplateType.CUSTOM
            ? customBackground
            : specialTemplate
          : commonTemplate,
      ]);
    });
    return Object.fromEntries(entries);
  }
);

export const getSpecialTemplateDict = createSelector(
  (state) => state.specialTemplateDict,
  (specialTemplateDict) => specialTemplateDict
);

export const getActiveCommonTemplate = createSelector(
  getCurrentActiveCommonTemplate,
  getSelectedCommonTemplate,
  (activeTemplate, selectedTemplate) => {
    return activeTemplate ? activeTemplate : selectedTemplate ? selectedTemplate : BlankTemplate;
  }
);

export const getTemplatesForAdminTool = createSelector(
  getCommonTemplates,
  getSpecialTemplates,
  (_, searchBy) => searchBy,
  (commonTemplates, specialTemplates, searchBy) => {
    const callbackFn =
      searchBy.keyword !== ''
        ? (template) =>
            template.templateType === TemplateType.DEFAULT &&
            template.designKeywords.toUpperCase().indexOf(searchBy.keyword.toUpperCase()) >= 0
        : (template) =>
            (template.templateType === TemplateType.DEFAULT || template.templateType === TemplateType.BLANK) &&
            template.categoryId === searchBy.categoryId;
    return [...specialTemplates.filter(callbackFn), ...commonTemplates];
  }
);
export const getTemplatePreviewUrlsFactory = (template, thumbnailWidth) => {
  return createSelector(
    getCurrentImprintAreaKey,
    (state) => state.ui.templatePreviewDict,
    (state) => state.canvasDict,
    getSelectedColorBomOptions,
    getItemUseScene7Template,
    (currentImprintAreaKey, templatePreviewDict, canvasDict, bomOptions, itemUseScene7Template) => {
      let previewUrls = { previews: [], previewSize: undefined, previewSizes: [], thumbnails: [] };
      if (templatePreviewDict !== undefined && templatePreviewDict !== null) {
        let templatePreviews = templatePreviewDict[currentImprintAreaKey];
        let defaultWidth = 0;
        let defaultHeight = 0;
        let previewUrl = '';
        let thumbnailUrl = '';
        let size = {};
        if (templatePreviews === undefined || templatePreviews.length === 0) {
          //no vignette preview
          let canvas = canvasDict[currentImprintAreaKey];
          defaultWidth = canvas.fullWidth;
          defaultHeight = canvas.fullHeight;
          size = getTemplatePreviewSize(defaultWidth, defaultHeight);
          thumbnailUrl = getTemplateThumbnailUrl(template.designId, thumbnailWidth);
          previewUrl = getTemplateCanvasUrl(template, size, itemUseScene7Template);
          previewUrls.previews.push(previewUrl);
          previewUrls.previewSizes.push(size);
          previewUrls.thumbnails.push(thumbnailUrl);
        } else {
          for (let templatePreview of templatePreviews) {
            let url = replaceFullPreviewUrlPlaceholder(templatePreview, bomOptions);
            defaultWidth = templatePreview.defaultWidth;
            defaultHeight = templatePreview.defaultHeight;
            const designIdPlaceHolder = templatePreview.placeHolder.designIdPlaceHolder;
            const folderIdPlaceHolder = templatePreview.placeHolder.folderIdPlaceHolder;
            const leftPlaceHolder = templatePreview.placeHolder.leftPlaceHolder;
            const rightPlaceHolder = templatePreview.placeHolder.rightPlaceHolder;
            let designIdWholePlaceHolder = leftPlaceHolder + designIdPlaceHolder + rightPlaceHolder;
            let folderIdWholePlaceHolder = leftPlaceHolder + folderIdPlaceHolder + rightPlaceHolder;
            if (template.templateId === 0) {
              url = url.replace(folderIdWholePlaceHolder, 'blank').replace(designIdWholePlaceHolder, 'template');
            } else {
              url = url
                .replace(designIdWholePlaceHolder, template.designId)
                .replace(folderIdWholePlaceHolder, template.folderId);
            }
            size = getTemplatePreviewSize(defaultWidth, defaultHeight);
            thumbnailUrl = url + '&wid=' + thumbnailWidth + '&hei=' + thumbnailWidth;
            previewUrl = url + '&wid=' + size.width + '&hei=' + size.height;
            previewUrls.previews.push(previewUrl);
            previewUrls.previewSizes.push(size);
            previewUrls.thumbnails.push(thumbnailUrl);
          }
        }
        previewUrls.previewSize = {
          height: Math.max(...previewUrls.previewSizes.map((size) => size.height)),
          width: Math.max(...previewUrls.previewSizes.map((size) => size.width)),
        };
      }
      return previewUrls;
    }
  );
};
const TEMPLATE_PREVIEW_MAX_HEIGHT = 350;
const TEMPLATE_PREVIEW_MAX_WIDTH = 400;
const getTemplatePreviewSize = (defaultWidth, defaultHeight) => {
  let size = { width: 0, height: 0 };
  if (defaultWidth > defaultHeight) {
    size.width = TEMPLATE_PREVIEW_MAX_WIDTH;
    size.height = parseInt((defaultHeight * TEMPLATE_PREVIEW_MAX_WIDTH) / defaultWidth);
  } else {
    size.width = TEMPLATE_PREVIEW_MAX_WIDTH;
    size.height = TEMPLATE_PREVIEW_MAX_HEIGHT;
  }
  return size;
};

export const getBackgroundStep = (state) => state.ui.backgroundStep;

export const getRecentBackroundList = createSelector(
  (state) => state.recentBackgroundDict,
  (state, imprintAreaKey) => imprintAreaKey || getCurrentImprintAreaKey(state),
  (recentBackgroundDict, imprintAreaKey) => recentBackgroundDict[imprintAreaKey]
);

export const getTempRecentBackground = (state) => state.ui.tempRecentBackground;
